import * as React from 'react';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import Collapse from '@mui/material/Collapse';
import PersonAdd from '@mui/icons-material/PersonAdd';
import Settings from '@mui/icons-material/Settings';
import Logout from '@mui/icons-material/Logout';
import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import NotificationsOffOutlinedIcon from '@mui/icons-material/NotificationsOffOutlined';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';

import styled from '@emotion/styled';

import { BasicFlex, LabelUpper } from '../../utils/Common';

import { truncate } from 'lodash';


// auth hook
import useAuth from '../../hooks/useAuth';

import useIsMobile from '../../hooks/useIsMobile';

import TimeClock, { TimeClockBadge, TimeClockChip, useTimeClock } from './Components/TimeClock';


const DisplayName = styled(BasicFlex)`
  width: fit-content;
  max-width: 18rem;
  justify-content: flex-end;
  font-size: 1.6rem;
`;

const notificationsSupported = () =>
  typeof window !== 'undefined' && 'Notification' in window;

// Order matters: Edge/Opera UAs contain "Chrome", iOS browsers all contain "Safari".
const detectBrowser = () => {
  if (typeof navigator === 'undefined') return 'unknown';
  const ua = navigator.userAgent;
  const iOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const android = /Android/.test(ua);

  if (iOS) return 'ios';
  if (android) return 'android';
  if (/Edg\//.test(ua)) return 'edge';
  if (/OPR\//.test(ua)) return 'opera';
  if (/Firefox\//.test(ua)) return 'firefox';
  if (/Chrome\//.test(ua)) return 'chrome';
  if (/Safari\//.test(ua)) return 'safari';
  return 'unknown';
};

const UNBLOCK_STEPS = {
  chrome: {
    label: 'Chrome',
    steps: [
      'Click the icon to the left of the web address (a lock, sliders, or tune icon).',
      'Choose "Site settings" or expand the Notifications row.',
      'Change Notifications from Block to Allow.',
      'Reload this page, then try enabling again.',
    ],
  },
  edge: {
    label: 'Edge',
    steps: [
      'Click the lock or icon to the left of the web address.',
      'Select "Permissions for this site".',
      'Change Notifications from Block to Allow.',
      'Reload this page, then try enabling again.',
    ],
  },
  opera: {
    label: 'Opera',
    steps: [
      'Click the icon to the left of the web address.',
      'Open "Site settings".',
      'Change Notifications from Block to Allow.',
      'Reload this page, then try enabling again.',
    ],
  },
  firefox: {
    label: 'Firefox',
    steps: [
      'Click the icon to the left of the web address.',
      'Find the blocked Notifications entry.',
      'Click the X or "Clear" next to it to remove the block.',
      'Reload this page, then try enabling again.',
    ],
  },
  safari: {
    label: 'Safari',
    steps: [
      'Open the Safari menu and choose Settings (or Preferences).',
      'Go to the Websites tab, then Notifications in the sidebar.',
      'Find this site in the list and set it to Allow.',
      'Reload this page, then try enabling again.',
    ],
  },
  android: {
    label: 'Android',
    steps: [
      'Tap the icon to the left of the web address.',
      'Tap Permissions, then Notifications.',
      'Switch Notifications to Allow.',
      'Reload this page, then try enabling again.',
    ],
  },
  ios: {
    label: 'iPhone / iPad',
    steps: [
      'This app must be added to your Home Screen for notifications to work.',
      'If it already is, open the Settings app.',
      'Scroll to this app in the list and tap it.',
      'Tap Notifications and switch Allow Notifications on.',
    ],
  },
  unknown: {
    label: 'your browser',
    steps: [
      'Open your browser settings and find the site permissions section.',
      'Locate this website in the list of sites.',
      'Change Notifications from Block to Allow.',
      'Reload this page, then try enabling again.',
    ],
  },
};

export default function AccountMenu() {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const { logout, user } = useAuth();
  let display_name = user?.display_name

  const { isMobile } = useIsMobile();

  const [permission, setPermission] = React.useState(() =>
    notificationsSupported() ? Notification.permission : 'unsupported'
  );
  const [helpOpen, setHelpOpen] = React.useState(false);

  // Time clock: used here only for the logout reminder (no ticking in this component)
  const { openEntry, clockOutAsync, isPunching } = useTimeClock();
  const [logoutPrompt, setLogoutPrompt] = React.useState(false);

  const handleClick = (event) => {
    // permission can change outside React (browser settings) — resync on open
    if (notificationsSupported()) setPermission(Notification.permission);
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
    setHelpOpen(false);
  };

  const userLogout = async () => {
    // Still on the clock -> ask before logging out
    if (openEntry) {
      handleClose()
      setLogoutPrompt(true)
      return
    }
    await logout()
    handleClose()
  }

  const logoutStayClockedIn = async () => {
    setLogoutPrompt(false)
    await logout()
  }

  const clockOutAndLogout = async () => {
    try {
      await clockOutAsync()
    } catch {
      return // error snackbar already shown; keep the dialog open
    }
    setLogoutPrompt(false)
    await logout()
  }

  const enableNotifications = async () => {
    if (!notificationsSupported()) return;
    try {
      const result = await Notification.requestPermission();
      setPermission(result);

      if (result === 'granted') {
        const reg = await navigator.serviceWorker?.ready;
        const options = {
          body: "You'll be alerted when new messages arrive.",
          icon: '/logo192.png',
          tag: 'novosalon-permission',
        };
        if (reg) reg.showNotification('Notifications enabled', options);
        else new Notification('Notifications enabled', options);
      }
    } catch (err) {
      console.error('Notification permission request failed', err);
    }
  };

  const toggleHelp = (event) => {
    event.stopPropagation(); // Menu's onClick would otherwise close everything
    setHelpOpen((prev) => !prev);
  };

  const blockedHelp = () => {
    const { label, steps } = UNBLOCK_STEPS[detectBrowser()];

    return (
      <Box onClick={(e) => e.stopPropagation()}>
        <MenuItem
          disableRipple
          onClick={toggleHelp}
          sx={{ cursor: 'default', '&:hover': { bgcolor: 'transparent' } }}
        >
          <ListItemIcon>
            <NotificationsOffOutlinedIcon fontSize="small" color="disabled" />
          </ListItemIcon>
          <Typography sx={{ flexGrow: 1, color: 'text.disabled' }}>
            Notifications blocked
          </Typography>
          <Tooltip title={helpOpen ? 'Hide steps' : 'How do I fix this?'}>
            <IconButton size="small" edge="end" onClick={toggleHelp}>
              {helpOpen ? (
                <ExpandLessRoundedIcon fontSize="small" />
              ) : (
                <HelpOutlineRoundedIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        </MenuItem>

        <Collapse in={helpOpen} timeout="auto" unmountOnExit>
          <Box
            sx={{
              mx: 2,
              mb: 1.5,
              p: 1.5,
              maxWidth: 320,
              borderRadius: 1,
              bgcolor: 'action.hover',
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              Re-enabling in {label}
            </Typography>
            <Box component="ol" sx={{ m: 0, pl: 2.5 }}>
              {steps.map((step, i) => (
                <Typography
                  component="li"
                  variant="body2"
                  key={i}
                  sx={{ mb: 0.75, whiteSpace: 'normal', lineHeight: 1.5 }}
                >
                  {step}
                </Typography>
              ))}
            </Box>
            <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
              Browsers only allow this change from their own settings, so it can't be
              re-requested from this page.
            </Typography>
          </Box>
        </Collapse>
      </Box>
    );
  };

  const notificationItem = () => {
    if (permission === 'unsupported') {
      return (
        <Tooltip title="Add this app to your home screen to enable notifications" placement="left">
          <Box>
            <MenuItem disabled>
              <ListItemIcon>
                <NotificationsOffOutlinedIcon fontSize="small" />
              </ListItemIcon>
              Notifications unavailable
            </MenuItem>
          </Box>
        </Tooltip>
      );
    }

    if (permission === 'granted') {
      return (
        <MenuItem disabled>
          <ListItemIcon>
            <NotificationsActiveOutlinedIcon fontSize="small" />
          </ListItemIcon>
          Notifications enabled
        </MenuItem>
      );
    }

    if (permission === 'denied') return blockedHelp();

    return (
      <MenuItem onClick={enableNotifications}>
        <ListItemIcon>
          <NotificationsNoneOutlinedIcon fontSize="small" />
        </ListItemIcon>
        Enable notifications
      </MenuItem>
    );
  };

  return (
    <React.Fragment>
      <Box sx={{ display: 'flex', alignItems: 'center', textAlign: 'center', gap: ".5rem", cursor: "pointer" }} onClick={handleClick}>
        {!isMobile && <TimeClockChip />}
        <Tooltip title="Account settings">
          <IconButton
            size="small"
            // sx={{ ml: 2 }}
            aria-controls={open ? 'account-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
          >
            <TimeClockBadge>
              <Avatar sx={{ width: 38, height: 38, bgcolor: "primary.main" }}>{display_name?.charAt(0)?.toUpperCase()}</Avatar>
            </TimeClockBadge>
          </IconButton>
        </Tooltip>
        {!isMobile && <DisplayName>{truncate(display_name, { length: 15})} <ArrowDropDownRoundedIcon /></DisplayName>}
      </Box>
      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              overflow: 'visible',
              filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
              mt: "1rem",
              '& .MuiAvatar-root': {
                width: 32,
                height: 32,
                ml: -0.5,
                mr: 1,
              },
              '&::before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: 'background.paper',
                transform: 'translateY(-50%) rotate(45deg)',
                zIndex: 0,
              },
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={handleClose}>
          <Avatar /> <LabelUpper>{display_name}</LabelUpper>
        </MenuItem>
        <Divider />
        {/* renders its own divider; hidden entirely when the user has no pay rate */}
        <TimeClock />
        {notificationItem()}
        <MenuItem onClick={handleClose}>
          <ListItemIcon>
            <Settings fontSize="small" />
          </ListItemIcon>
          Settings
        </MenuItem>
        <MenuItem onClick={userLogout}>
          <ListItemIcon>
            <Logout fontSize="small" />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>

      <Dialog open={logoutPrompt} onClose={() => setLogoutPrompt(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: '2rem' }}>You're still clocked in</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: '1.5rem' }}>
            You've been on the clock since{' '}
            <b>
              {openEntry &&
                new Date(openEntry.clock_in_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
            </b>
            . Clock out before logging out?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: '2.4rem', pb: '2rem', flexWrap: 'wrap', gap: '0.8rem', '& .MuiButton-root': { textTransform: 'none', fontSize: '1.4rem' } }}>
          <Button onClick={() => setLogoutPrompt(false)} disabled={isPunching}>
            Cancel
          </Button>
          <Button variant="outlined" onClick={logoutStayClockedIn} disabled={isPunching}>
            Log out, stay clocked in
          </Button>
          <Button variant="contained" disableElevation onClick={clockOutAndLogout} disabled={isPunching}>
            {isPunching ? 'Clocking out…' : 'Clock out & log out'}
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
}